/**
 * Control Portal - Global Device Inventory
 * Cross-tenant device management with bulk operations
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Wifi, WifiOff, Search, RefreshCw, Loader2, MoreVertical, 
  Battery, Thermometer, MapPin, Building2, Play, Pause,
  XCircle, ChevronRight, Settings, Download, CheckSquare
} from 'lucide-react';

export default function DeviceInventoryPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [devices, setDevices] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    tenant_id: 'all',
    status: 'all',
    search: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, devicesRes, tenantsRes] = await Promise.all([
        controlApi.getGlobalDeviceStats(),
        controlApi.getGlobalDevices({
          limit: 100,
          ...(filters.tenant_id !== 'all' && { tenant_id: filters.tenant_id }),
          ...(filters.status !== 'all' && { status: filters.status }),
          ...(filters.search && { search: filters.search })
        }),
        controlApi.getTenants()
      ]);
      setStats(statsRes.data);
      setDevices(devicesRes.data);
      setTenants(tenantsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters.tenant_id, filters.status]);

  const handleSearch = () => {
    fetchData();
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedDevices(devices.map(d => d.id));
    } else {
      setSelectedDevices([]);
    }
  };

  const handleSelectDevice = (deviceId, checked) => {
    if (checked) {
      setSelectedDevices([...selectedDevices, deviceId]);
    } else {
      setSelectedDevices(selectedDevices.filter(id => id !== deviceId));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedDevices.length === 0) {
      toast.error('No devices selected');
      return;
    }
    
    if (!confirm(`${action} ${selectedDevices.length} device(s)?`)) return;
    
    try {
      const res = await controlApi.bulkDeviceAction({
        device_ids: selectedDevices,
        action
      });
      toast.success(`${res.data.success_count} devices ${action}ed`);
      if (res.data.error_count > 0) {
        toast.error(`${res.data.error_count} devices failed`);
      }
      setSelectedDevices([]);
      fetchData();
    } catch (error) {
      toast.error(`Bulk ${action} failed`);
    }
  };

  const handleDeviceAction = async (device, action) => {
    try {
      if (action === 'suspend') {
        await controlApi.suspendGlobalDevice(device.id);
        toast.success(`Device ${device.device_uid} suspended`);
      } else if (action === 'resume') {
        await controlApi.resumeGlobalDevice(device.id);
        toast.success(`Device ${device.device_uid} resumed`);
      } else if (action === 'deactivate') {
        if (!confirm(`Deactivate ${device.device_uid}? This will revoke its license.`)) return;
        await controlApi.deactivateDevice(device.id);
        toast.success(`Device ${device.device_uid} deactivated`);
      }
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} device`);
    }
  };

  const getStatusBadge = (status, isOnline) => {
    if (status === 'active') {
      return isOnline 
        ? <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Online</Badge>
        : <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Offline</Badge>;
    }
    const styles = {
      inactive: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      suspended: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      revoked: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return <Badge className={styles[status] || styles.inactive}>{status}</Badge>;
  };

  return (
    <div className="p-6" data-testid="device-inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wifi className="w-7 h-7 text-blue-500" />
            Device Inventory
          </h1>
          <p className="text-slate-400 mt-1">Global device management across all tenants</p>
        </div>
        <div className="flex gap-2">
          {selectedDevices.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-blue-500 text-blue-400">
                  <CheckSquare className="w-4 h-4 mr-2" />
                  {selectedDevices.length} Selected
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-700">
                <DropdownMenuItem onClick={() => handleBulkAction('suspend')} className="text-amber-400 cursor-pointer">
                  <Pause className="w-4 h-4 mr-2" />
                  Suspend All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkAction('resume')} className="text-green-400 cursor-pointer">
                  <Play className="w-4 h-4 mr-2" />
                  Resume All
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={() => handleBulkAction('deactivate')} className="text-red-400 cursor-pointer">
                  <XCircle className="w-4 h-4 mr-2" />
                  Deactivate All
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" onClick={fetchData} className="border-slate-700 text-slate-300">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
              <Wifi className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.total_devices || 0}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
              <Wifi className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.online || 0}</p>
              <p className="text-slate-400 text-sm">Online</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-700">
              <WifiOff className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.offline || 0}</p>
              <p className="text-slate-400 text-sm">Offline</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
              <Pause className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.by_status?.suspended || 0}</p>
              <p className="text-slate-400 text-sm">Suspended</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/20">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.expiring_7_days || 0}</p>
              <p className="text-slate-400 text-sm">Expiring 7d</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <Select value={filters.tenant_id} onValueChange={(v) => setFilters({ ...filters, tenant_id: v })}>
          <SelectTrigger className="w-[200px] bg-slate-900 border-slate-700 text-white">
            <SelectValue placeholder="All Tenants" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Tenants</SelectItem>
            {tenants.map(t => (
              <SelectItem key={t.id} value={t.id} className="text-white">{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[150px] bg-slate-900 border-slate-700 text-white">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Statuses</SelectItem>
            <SelectItem value="active" className="text-white">Active</SelectItem>
            <SelectItem value="inactive" className="text-white">Inactive</SelectItem>
            <SelectItem value="suspended" className="text-white">Suspended</SelectItem>
            <SelectItem value="revoked" className="text-white">Revoked</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search by UID, IMEI, SIM..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10 bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
            Search
          </Button>
        </div>
      </div>

      {/* Device Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-slate-800/50">
              <TableHead className="w-10">
                <Checkbox
                  checked={selectedDevices.length === devices.length && devices.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead className="text-slate-400">Device</TableHead>
              <TableHead className="text-slate-400">Tenant</TableHead>
              <TableHead className="text-slate-400">Location</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">License</TableHead>
              <TableHead className="text-slate-400">Last Seen</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" />
                </TableCell>
              </TableRow>
            ) : devices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  <Wifi className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No devices found</p>
                </TableCell>
              </TableRow>
            ) : (
              devices.map((device) => (
                <TableRow
                  key={device.id}
                  className="border-slate-800 hover:bg-slate-800/50"
                  data-testid={`device-row-${device.device_uid}`}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedDevices.includes(device.id)}
                      onCheckedChange={(checked) => handleSelectDevice(device.id, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-white font-mono">{device.device_uid}</p>
                      <p className="text-slate-500 text-xs">{device.imei_number || '-'}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {device.tenant_name ? (
                      <Badge variant="outline" className="border-slate-600">{device.tenant_name}</Badge>
                    ) : (
                      <span className="text-slate-500">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {device.location_name || '-'}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(device.status, device.is_online)}
                  </TableCell>
                  <TableCell>
                    {device.entitlement ? (
                      <div>
                        <Badge variant="outline" className="border-blue-500/50 text-blue-400 mb-1">
                          {device.entitlement.plan_code}
                        </Badge>
                        <p className="text-slate-500 text-xs">
                          {device.entitlement.days_remaining !== null
                            ? `${device.entitlement.days_remaining}d left`
                            : 'No expiry'}
                        </p>
                      </div>
                    ) : (
                      <span className="text-slate-500">No license</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-400 text-sm">
                    {device.last_seen_at
                      ? new Date(device.last_seen_at).toLocaleString()
                      : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-slate-900 border-slate-700">
                        <DropdownMenuItem
                          className="text-white cursor-pointer"
                          onClick={() => navigate(`/control/devices/${device.id}`)}
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Control Panel
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-700" />
                        {device.status === 'active' ? (
                          <DropdownMenuItem
                            className="text-amber-400 cursor-pointer"
                            onClick={() => handleDeviceAction(device, 'suspend')}
                          >
                            <Pause className="w-4 h-4 mr-2" />
                            Suspend
                          </DropdownMenuItem>
                        ) : device.status === 'suspended' && (
                          <DropdownMenuItem
                            className="text-green-400 cursor-pointer"
                            onClick={() => handleDeviceAction(device, 'resume')}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Resume
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-red-400 cursor-pointer"
                          onClick={() => handleDeviceAction(device, 'deactivate')}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Deactivate
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
